<script setup>
import { ref, getCurrentInstance } from 'vue'
import { DriverStore } from '../pinia/Driver.js'

const Driver = DriverStore()
const { proxy } = getCurrentInstance()

const snackbar = ref(false)
const error = ref('')
const raceAgainEnabled = ref(Driver.user.raceAgainEnabled == 1 || Driver.user.raceAgainEnabled === undefined)
const raceAgainMode = ref(parseInt(Driver.user.raceAgainMode) || 0)

const raceAgainModeOptions = [
    { 
        value: 0, 
        title: 'Random Race',
        label: 'Random Race',
        description: 'Start a new random race each time'
    },
    { 
        value: 1, 
        title: 'Same Race',
        label: 'Same Race',
        description: 'Repeat the exact same race'
    }
]

async function save() {
    let saved = false
    error.value = ''

    // Save Race Again enabled/disabled
    const raceAgainEnabledValue = raceAgainEnabled.value ? 1 : 0
    if(raceAgainEnabledValue != Driver.user.raceAgainEnabled) {
        try {
            const response = await proxy.$http.post("", JSON.stringify({ 
                serviceName: "account", 
                methodName: "UpdateRaceAgainEnabled", 
                parameters: [raceAgainEnabledValue] 
            }))
            
            Driver.user.raceAgainEnabled = raceAgainEnabledValue
            saved = true
        } catch (err) {
            error.value = err.response?.data?.error || 'Error saving Race Again enabled setting'
            console.error('UpdateRaceAgainEnabled error:', err)
            return
        }
    }

    // Save Race Again mode
    const raceAgainModeValue = parseInt(raceAgainMode.value)
    if(raceAgainModeValue != parseInt(Driver.user.raceAgainMode || 0)) {
        try {
            const response = await proxy.$http.post("", JSON.stringify({ 
                serviceName: "account", 
                methodName: "UpdateRaceAgainMode", 
                parameters: [raceAgainModeValue] 
            }))
            
            Driver.user.raceAgainMode = raceAgainModeValue
            saved = true
        } catch (err) {
            error.value = err.response?.data?.error || 'Error saving Race Again mode setting'
            console.error('UpdateRaceAgainMode error:', err)
            return
        }
    }

    // Show success notification
    if (saved) {
        snackbar.value = true
    }
}
</script>

<template>
<div class="gameSettingsForm">
    <v-snackbar
        :timeout="2000"
        color="deep-purple-accent-4"
        elevation="24"
        v-model="snackbar"
    >
        Your game settings have been saved.
    </v-snackbar>

    <v-alert class="error" v-if="error" :text="error" type="error"></v-alert>

    <v-form>
        <v-container>
            <!-- Race Again Settings Card -->
            <div class="settings-card">
                <div class="settings-card-header">
                    <div class="header-icon">
                        <v-icon size="28">mdi-restart</v-icon>
                    </div>
                    <div class="header-content">
                        <h3 class="settings-title">Race Again</h3>
                        <p class="settings-subtitle">Configure quick restart behavior</p>
                    </div>
                </div>

                <div class="settings-card-body">
                    <!-- Enable/Disable Toggle -->
                    <div class="setting-item">
                        <div class="setting-info">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-power</v-icon>
                                Enable Feature
                            </div>
                            <div class="setting-description">
                                Allow quick restart after completing a race
                            </div>
                        </div>
                        <v-switch
                            v-model="raceAgainEnabled"
                            color="primary"
                            hide-details
                            density="comfortable"
                            class="setting-switch"
                        ></v-switch>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <!-- Mode Selection -->
                    <div class="setting-item">
                        <div class="setting-info full-width">
                            <div class="setting-label">
                                <v-icon size="20" class="mr-2">mdi-racing-helmet</v-icon>
                                Race Again Mode
                            </div>
                            <div class="setting-description">
                                Choose how Race Again behaves
                            </div>
                            
                            <div class="mode-options" :class="{ disabled: !raceAgainEnabled }">
                                <div 
                                    v-for="option in raceAgainModeOptions" 
                                    :key="option.value"
                                    class="mode-option"
                                    :class="{ 
                                        active: raceAgainMode === option.value,
                                        disabled: !raceAgainEnabled 
                                    }"
                                    @click="!raceAgainEnabled ? null : raceAgainMode = option.value"
                                >
                                    <div class="mode-radio">
                                        <v-icon v-if="raceAgainMode === option.value" size="18">mdi-radiobox-marked</v-icon>
                                        <v-icon v-else size="18">mdi-radiobox-blank</v-icon>
                                    </div>
                                    <div class="mode-content">
                                        <div class="mode-icon">
                                            <v-icon size="24">{{ option.value === 0 ? 'mdi-shuffle-variant' : 'mdi-replay' }}</v-icon>
                                        </div>
                                        <div class="mode-text">
                                            <div class="mode-title">{{ option.title }}</div>
                                            <div class="mode-desc">{{ option.description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <v-btn 
                class="save-btn" 
                variant="elevated" 
                color="primary"
                size="large"
                @click="save"
                block
            >
                <v-icon class="mr-2">mdi-content-save</v-icon>
                Save Settings
            </v-btn>
        </v-container>
    </v-form>
</div>
</template>

<style scoped>
.gameSettingsForm {
    margin: 0;
    padding: 0;
}

.gameSettingsForm .v-icon {
    color: currentColor;
    opacity: 1;
}

.settings-card {
    background: rgba(255, 255, 255, 0.03);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
    margin-bottom: 20px;
}

.settings-card-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 20px;
    background: rgba(var(--v-theme-primary), 0.1);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.header-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    background: rgba(var(--v-theme-primary), 0.2);
    border-radius: 10px;
}

.header-icon .v-icon {
    color: rgb(var(--v-theme-primary));
}

.header-content {
    flex: 1;
}

.settings-title {
    margin: 0;
    font-size: 1.3em;
    font-weight: 600;
    color: rgb(var(--v-theme-primary));
}

.settings-subtitle {
    margin: 4px 0 0 0;
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.6);
}

.settings-card-body {
    padding: 24px;
}

.setting-item {
    display: flex;
    align-items: flex-start;
    gap: 16px;
}

.setting-info {
    flex: 1;
}

.setting-info.full-width {
    width: 100%;
}

.setting-label {
    display: flex;
    align-items: center;
    font-size: 1em;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 6px;
}

.setting-label .v-icon {
    color: rgba(255, 255, 255, 0.9);
}

.setting-description {
    font-size: 0.85em;
    color: rgba(255, 255, 255, 0.5);
    margin-bottom: 16px;
}

.setting-switch {
    flex-shrink: 0;
}

.mode-options {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
}

.mode-options.disabled {
    opacity: 0.4;
    pointer-events: none;
}

.mode-option {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.02);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.mode-option:hover:not(.disabled) {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(var(--v-theme-primary), 0.5);
    transform: translateX(4px);
}

.mode-option.active {
    background: rgba(var(--v-theme-primary), 0.15);
    border-color: rgb(var(--v-theme-primary));
}

.mode-option.disabled {
    cursor: not-allowed;
}

.mode-radio {
    display: flex;
    align-items: center;
}

.mode-radio .v-icon {
    color: rgba(255, 255, 255, 0.7);
}

.mode-option.active .mode-radio .v-icon {
    color: rgb(var(--v-theme-primary)) !important;
}

.mode-content {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
}

.mode-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 8px;
}

.mode-icon .v-icon {
    color: rgba(255, 255, 255, 0.9);
    transition: color 0.2s ease;
}

.mode-option.active .mode-icon {
    background: rgba(var(--v-theme-primary), 0.2);
}

.mode-option.active .mode-icon .v-icon {
    color: rgb(var(--v-theme-primary)) !important;
}

.mode-text {
    flex: 1;
}

.mode-title {
    font-size: 1em;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 4px;
}

.mode-desc {
    font-size: 0.8em;
    color: rgba(255, 255, 255, 0.5);
}

.save-btn {
    margin-top: 8px;
    font-weight: 600;
    letter-spacing: 0.5px;
}

.save-btn .v-icon {
    opacity: 1 !important;
}

.error {
    margin-bottom: 20px !important;
}
</style>
<style>
.v-input__details {
    padding-inline-start: 2px!important;
}
</style>
